import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Hidden from "@material-ui/core/Hidden";
import { makeStyles } from "@material-ui/core/styles";
import TicketsManagerTabs from "../../components/TicketsManagerTabs";
import Ticket from "../../components/Ticket";

import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const defaultTicketsManagerWidth = 550;
const minTicketsManagerWidth = 404;
const maxTicketsManagerWidth = 700;

const useStyles = makeStyles((theme) => ({
	chatContainer: {
		flex: 1,
		padding: 12,
		height: `calc(100% - 48px)`,
		overflowY: "hidden",
		background:
			theme.mode === "light"
				? "linear-gradient(180deg, rgba(248,250,252,0.94) 0%, rgba(241,245,249,0.82) 100%)"
				: "linear-gradient(180deg, rgba(15,23,42,0.82) 0%, rgba(2,6,23,0.88) 100%)",
	},
	chatPapper: {
		display: "flex",
		height: "100%",
		gap: 12,
	},
	contactsWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflowY: "hidden",
		position: "relative",
		minWidth: `${minTicketsManagerWidth}px`,
		borderRadius: 24,
		background:
			theme.mode === "light"
				? "rgba(255,255,255,0.92)"
				: "rgba(15,23,42,0.78)",
		border: `1px solid ${theme.mode === "light" ? "rgba(148,163,184,0.18)" : "rgba(255,255,255,0.08)"}`,
		boxShadow:
			theme.mode === "light"
				? "0 16px 40px rgba(15, 23, 42, 0.08)"
				: "0 16px 42px rgba(2, 6, 23, 0.34)",
		backdropFilter: "blur(10px)",
	},
	messagesWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		flexGrow: 1,
		borderRadius: 24,
		overflow: "hidden",
		background:
			theme.mode === "light"
				? "rgba(255,255,255,0.9)"
				: "rgba(15,23,42,0.78)",
		border: `1px solid ${theme.mode === "light" ? "rgba(148,163,184,0.18)" : "rgba(255,255,255,0.08)"}`,
		boxShadow:
			theme.mode === "light"
				? "0 16px 40px rgba(15, 23, 42, 0.06)"
				: "0 16px 42px rgba(2, 6, 23, 0.32)",
	},
	welcomeMsg: {
		background: theme.palette.tabHeaderBackground,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		flexDirection: "column",
		gap: 18,
		height: "100%",
		textAlign: "center",
		borderRadius: 24,
		padding: theme.spacing(4),
	},
	dragger: {
		width: "12px",
		cursor: "ew-resize",
		position: "absolute",
		top: 12,
		right: -6,
		bottom: 12,
		zIndex: 100,
		background: "transparent",
		userSelect: "none",
		"&::before": {
			content: '""',
			position: "absolute",
			top: "50%",
			left: "50%",
			transform: "translate(-50%, -50%)",
			width: 4,
			height: 72,
			borderRadius: 999,
			background:
				theme.mode === "light"
					? "linear-gradient(180deg, rgba(148,163,184,0.45), rgba(100,116,139,0.85))"
					: "linear-gradient(180deg, rgba(148,163,184,0.25), rgba(226,232,240,0.55))",
			boxShadow: "0 4px 18px rgba(15, 23, 42, 0.16)",
		},
	},
	logo: {
		logo: theme.logo,
		content: "url(" + (theme.mode === "light"
			? theme.calculatedLogoLight()
			: theme.calculatedLogoDark()) + ")",
		maxWidth: 280,
		filter: theme.mode === "light" ? "drop-shadow(0 8px 18px rgba(15, 23, 42, 0.08))" : "drop-shadow(0 10px 22px rgba(2, 6, 23, 0.32))",
	},
	"@media (max-width: 960px)": {
		chatContainer: {
			padding: 8,
		},
		chatPapper: {
			gap: 8,
		},
		contactsWrapper: {
			borderRadius: 20,
		},
		messagesWrapper: {
			borderRadius: 20,
		},
	},
}));

const TicketsCustom = () => {
	const { user } = useContext(AuthContext);
	
	// ⚠️ CORREÇÃO PRINCIPAL: Inicializar com largura padrão adequada
	const [ticketsManagerWidth, setTicketsManagerWidth] = useState(
		user?.defaultTicketsManagerWidth || defaultTicketsManagerWidth
	);
	
	const classes = useStyles({ ticketsManagerWidth });
	const { ticketId } = useParams();
	const ticketsManagerWidthRef = useRef(ticketsManagerWidth);

	// ⚠️ CORREÇÃO: useEffect mais robusto para inicialização
	useEffect(() => {
		// Definir largura baseada no usuário ou padrão
		const initialWidth = user?.defaultTicketsManagerWidth || defaultTicketsManagerWidth;
		
		// Garantir que a largura esteja dentro dos limites
		const validWidth = Math.max(
			minTicketsManagerWidth,
			Math.min(maxTicketsManagerWidth, initialWidth)
		);
		
		setTicketsManagerWidth(validWidth);
		ticketsManagerWidthRef.current = validWidth;
	}, [user]);

	const handleMouseDown = (e) => {
		document.addEventListener("mouseup", handleMouseUp, true);
		document.addEventListener("mousemove", handleMouseMove, true);
	};

	const handleSaveContact = async (value) => {
		// Garantir largura mínima antes de salvar
		const validValue = Math.max(minTicketsManagerWidth, value);
		
		try {
			await api.put(`/users/toggleChangeWidht/${user.id}`, { 
				defaultTicketsManagerWidth: validValue 
			});
		} catch (error) {
			console.error("Erro ao salvar largura:", error);
		}
	};

	const handleMouseMove = useCallback((e) => {
		const newWidth = e.clientX - document.body.offsetLeft;
		
		if (newWidth >= minTicketsManagerWidth && newWidth <= maxTicketsManagerWidth) {
			ticketsManagerWidthRef.current = newWidth;
			setTicketsManagerWidth(newWidth);
		}
	}, []);

	const handleMouseUp = async () => {
		document.removeEventListener("mouseup", handleMouseUp, true);
		document.removeEventListener("mousemove", handleMouseMove, true);

		const newWidth = ticketsManagerWidthRef.current;

		if (newWidth !== ticketsManagerWidth) {
			await handleSaveContact(newWidth);
		}
	};

	// ⚠️ CORREÇÃO: Garantir que a largura nunca seja 0 ou inválida
	const effectiveWidth = Math.max(minTicketsManagerWidth, ticketsManagerWidth);

	return (
		<QueueSelectedProvider>
			<div className={classes.chatContainer}>
				<div className={classes.chatPapper}>
					<div
						className={classes.contactsWrapper}
						style={{ 
							width: `${effectiveWidth}px`,
							// Adicionar fallbacks importantes
							minWidth: `${minTicketsManagerWidth}px`,
							maxWidth: `${maxTicketsManagerWidth}px`,
							// Garantir visibilidade
							opacity: effectiveWidth > 0 ? 1 : 0,
							visibility: effectiveWidth > 0 ? 'visible' : 'hidden'
						}}
					>
						<TicketsManagerTabs />
						<div 
							onMouseDown={handleMouseDown} 
							className={classes.dragger} 
						/>
					</div>
					<div className={classes.messagesWrapper}>
						{ticketId ? (
							<Ticket />
						) : (
							<Hidden only={["sm", "xs"]}>
								<Paper square variant="outlined" className={classes.welcomeMsg}>
									<span>
										<center>
											<img className={classes.logo} width="50%" alt="" />
										</center>
										{i18n.t("chat.noTicketMessage")}
									</span>
								</Paper>
							</Hidden>
						)}
					</div>
				</div>
			</div>
		</QueueSelectedProvider>
	);
};

export default TicketsCustom;